import { z } from "zod";
import type { MoreRoute } from "./route.ts";

/*
 * The Bounces folder's API (LEA-311, docs/2026-10-26-bounces-decision.md section 8). Browser-safe:
 * the web reads these too. Rows are read-only: no Done, Snooze, label, draft or bulk action, and no
 * number here enters a work count.
 */

const BOUNCE_KINDS = ["hard", "soft", "unplaced"] as const;
const BounceKindSchema = z.enum(BOUNCE_KINDS);
export const BOUNCES_PAGE_SIZE = 50;

export const BouncesQuerySchema = z.strictObject({
  /** Hard or Soft; absent is every bounce, unplaced ones included. */
  kind: z.enum(["hard", "soft"]).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
});

const BounceRowSchema = z.strictObject({
  ref: z.string().regex(/^[1-9]\d*$/),
  sender: z.string(),
  subject: z.string(),
  receivedAt: z.iso.datetime({ offset: true }),
  kind: BounceKindSchema,
  /** Bison's own page for the message; null when the record carried no uuid. */
  bisonUrl: z.url().nullable(),
});
export type BounceRow = z.infer<typeof BounceRowSchema>;

export const BouncesPageSchema = z.strictObject({
  rows: z.array(BounceRowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.literal(BOUNCES_PAGE_SIZE),
  /** The one quiet number inside the view: bounces that arrived in the last 7 days. */
  lastSevenDays: z.number().int().nonnegative(),
  /** False until the first 90-day read of every folder feeding Bounces has finished, whole. */
  complete: z.boolean(),
  /**
   * A folder held more than Bison returns to one read (15,000 messages): its oldest bounces in the
   * window are only in Bison.
   */
  capped: z.boolean(),
});
export type BouncesPage = z.infer<typeof BouncesPageSchema>;

export const BounceMessageSchema = z.discriminatedUnion("state", [
  BounceRowSchema.extend({ state: z.literal("ready"), text: z.string() }),
  /** Bison could not be read now: the row's own fields, and the way to Bison. */
  BounceRowSchema.extend({ state: z.literal("source_unavailable") }),
]);
export type BounceMessage = z.infer<typeof BounceMessageSchema>;

/*
 * The More group's other folders (LEA-314, docs/2026-10-27-more-folders-build-plan.md design 5):
 * read-only like Bounces, with no kind and no seven-day number. Each is named by its path segment,
 * which its API routes and its page's `?folder=` address both use.
 */
export const PLAIN_MORE_FOLDERS = {
  "auto-replies": "auto_replies",
  "other-campaigns": "other_campaigns",
  spam: "spam",
  untracked: "untracked",
} as const satisfies Record<string, Exclude<MoreRoute, "bounces">>;
export type PlainMoreFolder = keyof typeof PLAIN_MORE_FOLDERS;
export const MORE_FOLDER_PAGE_SIZE = 50;

export const MoreFolderQuerySchema = z.strictObject({
  page: z.coerce.number().int().min(1).max(10_000).default(1),
});

const MoreRowSchema = BounceRowSchema.omit({ kind: true });
export type MoreRow = z.infer<typeof MoreRowSchema>;

export const MoreFolderPageSchema = z.strictObject({
  rows: z.array(MoreRowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.literal(MORE_FOLDER_PAGE_SIZE),
  /** False until the first 90-day read of every folder feeding this one has finished, whole. */
  complete: z.boolean(),
  /** A feeding folder held more than Bison returns to one read: its oldest mail is only in Bison. */
  capped: z.boolean(),
});
export type MoreFolderPage = z.infer<typeof MoreFolderPageSchema>;

export const MoreMessageSchema = z.discriminatedUnion("state", [
  MoreRowSchema.extend({ state: z.literal("ready"), text: z.string() }),
  MoreRowSchema.extend({ state: z.literal("source_unavailable") }),
]);
export type MoreMessage = z.infer<typeof MoreMessageSchema>;
